import { useEffect } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui/Modal';
import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import redcrossLogo from '#src/assets/starter/endorsements/redcross-logo.png';

import { EndorsementCard } from '../EndorsementCard';

import { contentColumn, modalBody } from './EndorsementModal.styles';
import { HelpModalList } from './HelpModalList';
import { SubmitModalContent } from './SubmitModalContent';
import type { EndorsementFormData } from './types';

export type EndorsementModalProps =
	| {
			variant: 'help';
			isOpen: boolean;
			onClose: () => void;
			formData?: EndorsementFormData;
			isSubmitting?: never;
			onConfirm?: never;
			isTermsAgreed?: never;
			onTermsChange?: never;
	  }
	| {
			variant: 'submit';
			isOpen: boolean;
			onClose: () => void;
			onConfirm: () => void;
			formData: EndorsementFormData;
			isSubmitting?: boolean;
			isTermsAgreed: boolean;
			onTermsChange: (checked: boolean) => void;
	  };

const HELP_MODAL_PREVIEW_DATA: EndorsementFormData = {
	name: 'American Red Cross',
	title: 'A non-profit humanitarian organization',
	statement: `The American Red Cross sees how this issue directly affects people's safety and access to help during emergencies, especially for those already at risk.

"This petition highlights a real need, and taking action now will help protect vulnerable communities and strengthen our ability to respond when people need help most."`,
	photoUrl: redcrossLogo,
	url: 'https://www.redcross.org/',
};

const ModalContent: React.FC<{
	variant: 'help' | 'submit';
	formData?: EndorsementFormData;
}> = ({ variant, formData }) => {
	const previewData = variant === 'help' ? HELP_MODAL_PREVIEW_DATA : formData;

	return (
		<div className="order-1 flex-1 md:order-2">
			<EndorsementCard
				name={previewData?.name || ''}
				title={previewData?.title || ''}
				description={previewData?.statement}
				photoUrl={previewData?.photoUrl || ''}
				link={previewData?.url}
				descriptionLineClamp={7}
			/>
		</div>
	);
};

const ModalFooterContent: React.FC<EndorsementModalProps> = (props) => {
	const { variant, onClose, onConfirm, isSubmitting } = props;
	const isHelpVariant = variant === 'help';
	const submitting = variant === 'submit' ? (isSubmitting ?? false) : false;

	if (isHelpVariant) {
		return (
			<div className="w-full md:w-auto">
				<Button
					onClick={onClose}
					variant="primary"
					className="w-full"
					data-testid="help-modal-close-button"
					data-qa="help-modal-close-button"
				>
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.help_modal.close_button" />
				</Button>
			</div>
		);
	}

	return (
		<Button
			onClick={onConfirm}
			variant="primary"
			className="w-full"
			disabled={submitting}
			data-testid="submit-modal-confirm-button"
			data-qa="submit-modal-confirm-button"
		>
			<Translate value="corgi.petition.starter_dashboard_v2.endorsements.submit_modal.add_endorsement" />
		</Button>
	);
};

export const EndorsementModal: React.FC<EndorsementModalProps> = (props) => {
	const { variant, isOpen, onClose, formData, isTermsAgreed, onTermsChange } = props;
	const isHelpVariant = variant === 'help';
	const track = useTracking();
	const { translate } = useI18n();

	useEffect(() => {
		if (variant === 'submit' && isOpen) {
			void track('petition_manage_confirm_endorsement_modal_view');
		}
	}, [variant, isOpen, track]);

	return (
		<Modal
			isOpen={isOpen}
			onOpenChange={onClose}
			isDismissable
			onClose={onClose}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			data-testid={`endorsement-${variant}-modal`}
			size="large"
			className={cn(
				'z-30 [&_[data-modal-body]+div]:border-t-0! [&_[data-modal-body]+div]:pt-0!',
				isHelpVariant ? 'md:[&>div>div]:max-h-[690px]!' : 'md:[&>div>div]:max-h-[570px]!',
			)}
		>
			<ModalHeader
				title={
					isHelpVariant ? (
						<Text className="font-bold">
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.help_modal.title" />
						</Text>
					) : (
						<Text className="font-bold">
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.submit_modal.title" />
						</Text>
					)
				}
			/>
			<ModalBody className={modalBody()}>
				{isHelpVariant && (
					<Text variant="body" className="text-typography-weak">
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.help_modal.subtitle" />
					</Text>
				)}
				<div className="flex w-full flex-col gap-6 md:flex-row md:gap-8">
					<div className={cn(contentColumn(), 'order-2 md:order-1')}>
						{isHelpVariant ? (
							<HelpModalList />
						) : (
							<SubmitModalContent isTermsAgreed={isTermsAgreed} onTermsChange={onTermsChange} />
						)}
					</div>
					<ModalContent variant={variant} formData={formData} />
				</div>
			</ModalBody>
			<ModalFooter className="rounded-b-3 border-stroke-weaker bg-fill-inverse-strong border-t pt-4 pb-0 lg:flex lg:justify-end">
				<ModalFooterContent {...props} />
			</ModalFooter>
		</Modal>
	);
};
